import { ReactNode } from 'react';
import { cn } from '@ifixit/ui/lib/utils';
import { useBreakpoint } from '@ifixit/ui/hooks/use-media-query';
import { BreadCrumbs } from '@ifixit/carpenter-breadcrumbs/Breadcrumbs';
import { FlexScrollGradient } from 'Guide/Guide/GuideProducts/FlexScrollGradient';

export interface NavData {
   breadCrumbs?: Array<BreadcrumbData>;
   navTabs?: Array<NavTabData>;
   editMenuComponent?: ReactNode;
}

export interface BreadcrumbData {
   name: string;
   url: string;
}

interface NavTabData {
   isCurrentPage?: boolean;
   name: string;
   url?: string;
}

export function SharedNavBar(props: NavData) {
   const { breadCrumbs, navTabs, editMenuComponent } = props;
   const isAboveSm = useBreakpoint('sm');
   const isMobile = isAboveSm === false;

   return (
      <div className="flex w-full flex-col">
         <div className="flex justify-center border-b border-gray-300">
            <div className="flex h-12 w-full max-w-[1280px] grow justify-between px-0 sm:px-5 lg:px-8">
               {breadCrumbs && !isMobile && (
                  <div className="flex shrink-0 grow basis-0">
                     <BreadCrumbs breadCrumbs={breadCrumbs} />
                  </div>
               )}
               {navTabs && <NavTabs navTabs={navTabs} />}
               {editMenuComponent}
            </div>
         </div>
         {breadCrumbs && isMobile && <BreadCrumbs breadCrumbs={breadCrumbs} paddingX={4} />}
      </div>
   );
}

const baseTabClass =
   'relative flex items-center justify-center ps-4 pe-4 pt-2 pb-2 text-sm font-normal no-underline outline outline-2 outline-offset-2 outline-transparent';

// Selected tab: bottom underline via ::after, darkens on hover.
const selectedTabClass = cn(
   baseTabClass,
   "font-medium text-gray-900 hover:bg-gray-100 hover:no-underline after:absolute after:inset-x-0 after:bottom-0 after:h-[3px] after:rounded-t-[2px] after:bg-brand-500 after:content-[''] hover:after:bg-brand-700"
);

// Unselected, linked tab: lifts to gray-700 on hover.
const notSelectedTabClass = cn(
   baseTabClass,
   'text-gray-500 hover:bg-gray-100 hover:text-gray-700 hover:no-underline'
);

// Unselected, unlinked tab (no url): rendered disabled.
const disabledTabClass = cn(
   baseTabClass,
   'cursor-not-allowed bg-gray-100 text-gray-700 opacity-40'
);

function NavTabs({ navTabs }: { navTabs: NavTabData[] }) {
   return (
      <FlexScrollGradient className="h-full shrink overflow-hidden">
         {navTabs.map((tab: NavTabData) =>
            tab.isCurrentPage ? (
               tab.url ? (
                  <a key={tab.name} className={selectedTabClass} href={tab.url}>
                     {tab.name}
                  </a>
               ) : (
                  <div key={tab.name} className={selectedTabClass}>
                     {tab.name}
                  </div>
               )
            ) : tab.url ? (
               <a key={tab.name} className={notSelectedTabClass} href={tab.url}>
                  {tab.name}
               </a>
            ) : (
               <div key={tab.name} className={disabledTabClass}>
                  {tab.name}
               </div>
            )
         )}
      </FlexScrollGradient>
   );
}
